import Axios, { AxiosRequestConfig, CancelToken } from 'axios';
import { HandleLoadingProgress } from 'utils/sagas/uploading';

type Options = {
  handleProgress?: (progress: number) => void;
  handleError?: (e: Error) => void;
  cancelToken?: CancelToken;
};

const MAX_OLD_FILES_LENGTH = 5;

const loading: { [key: string]: Promise<Blob> } = {};
const progress: { [key: string]: HandleLoadingProgress[] } = {};
const numOfRunning: { [key: string]: number } = {};
const files: { [key: string]: string } = {};
const usedFiles: { [key: string]: [string, string] } = {};
let oldFiles: string[] = [];

const loadFile = async (
  url: string,
  { handleError, handleProgress, cancelToken }: Options
): Promise<Blob> => {
  if (!loading[url]) {
    numOfRunning[url] = 1;
    progress[url] = [];

    if (handleProgress) {
      progress[url].push(handleProgress);
    }

    const onDownloadProgress: AxiosRequestConfig['onDownloadProgress'] = e => {
      const percent = Math.round((e.loaded * 100) / e.total);
      progress[url].forEach(func => func(percent));
    };

    loading[url] = Axios.get(url, {
      responseType: 'blob',
      onDownloadProgress,
      cancelToken
    })
      .then(res => res.data)
      .catch(e => {
        if (handleError) handleError(e);
      })
      .finally(() => {
        delete loading[url];
        delete numOfRunning[url];
        progress[url] = [];
      });
  } else if (handleProgress) {
    numOfRunning[url] += 1;
    progress[url].push(handleProgress);
  }

  return loading[url];
};

export const isCancellable = (url: string) => {
  numOfRunning[url] -= 1;
  return numOfRunning[url] === 0;
};

export const isFileStoredLocally = (file: File): boolean => {
  return !!usedFiles[getFileUniqueId(file)];
};

export const storeFileInLocal = (file: File) => {
  return URL.createObjectURL(file);
};

export const storeFileLocal = (file: File, localSrc: string, externalSrc: string) => {
  const uniqueId = getFileUniqueId(file);
  usedFiles[uniqueId] = [localSrc, externalSrc];
};

export const getLocalFile = (file: File) => {
  const uniqueId = getFileUniqueId(file);
  return usedFiles[uniqueId];
};

export const deleteLocalFile = (src: string) => {
  for (const key in usedFiles) {
    if (usedFiles[key][0] === src) {
      delete usedFiles[key];
    }
  }
};

export const storeFile = async (url: string, options: Options = {}): Promise<string> => {
  const blob = await loadFile(url, options);
  if (blob) {
    files[url] = URL.createObjectURL(blob);
  }

  return files[url];
};

export const deleteFile = (url: string): void => {
  if (!files[url]) return;
  if (oldFiles.indexOf(url) !== -1) return;

  oldFiles.unshift(url);

  while (oldFiles.length > MAX_OLD_FILES_LENGTH) {
    const deletedUrl = oldFiles.pop();
    URL.revokeObjectURL(deletedUrl);
    delete files[deletedUrl];
    deleteLocalFile(deletedUrl);
  }
};

export const isFileCached = (url: string) => files[url];

export const getFile = async (url: string, options: Options = {}): Promise<string> => {
  if (files[url]) {
    if (options.handleProgress) options.handleProgress(100);
    return files[url];
  }
  return storeFile(url, options);
};

export const getSrc = (blobSrc: string): string | null =>
  Object.keys(files).find(item => files[item] === blobSrc);

export const restoreFiles = (urls: string[]): void => {
  oldFiles = oldFiles.filter(item => urls.includes(item));
};

export const getFileUniqueId = (file: File): string => {
  return `${file.name}-${file.lastModified}-${file.size}`;
};
